/**
 * GalleryMasonry — explicit flex-column masonry with scroll-driven GSAP animations.
 *
 * Entry:  clip-path wipe per item + headline word-lift.
 * Scroll: per-column parallax drift (different speeds per column) + per-image zoom scrub.
 *
 * Registered as: gallery / gallery-masonry
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface MasonryPhoto {
  src: string;
  alt?: string;
  caption?: string;
}

interface GalleryMasonryProps {
  headline?: string;
  subheadline?: string;
  photos?: MasonryPhoto[];
  /** Number of columns (default: 3) */
  columns?: 2 | 3;
}

const DEFAULT_PHOTOS: MasonryPhoto[] = Array.from({ length: 8 }, (_, i) => ({
  src: "",
  alt: `Photo ${i + 1}`,
}));

export function GalleryMasonry({ props }: SectionComponentProps) {
  const {
    headline = "Our Work",
    subheadline,
    photos = DEFAULT_PHOTOS,
    columns = 3,
  } = props as GalleryMasonryProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {

      // ── Entry: headline word-lift ────────────────────────────────────────
      gsap.fromTo(".masonry-word",
        { yPercent: 115, opacity: 0 },
        {
          yPercent: 0, opacity: 1,
          duration: 0.75, ease: "power3.out", stagger: 0.08,
          scrollTrigger: { trigger: ".masonry-header", start: "top 88%", toggleActions: "play none none none" },
        }
      );

      gsap.fromTo(".masonry-sub",
        { opacity: 0, y: 14 },
        {
          opacity: 1, y: 0, duration: 0.6, ease: "power3.out",
          scrollTrigger: { trigger: ".masonry-header", start: "top 82%", toggleActions: "play none none none" },
        }
      );

      // ── Entry: clip-path wipe per item ───────────────────────────────────
      gsap.fromTo(".masonry-item",
        { clipPath: "inset(0 0 100% 0)" },
        {
          clipPath: "inset(0 0 0% 0)",
          duration: 1.05, ease: "power3.inOut",
          stagger: { each: 0.13, from: "start" },
          scrollTrigger: { trigger: ".masonry-grid", start: "top 82%", toggleActions: "play none none none" },
        }
      );

      // yPercent-only parallax on entry — scale is reserved for the scroll scrub below
      gsap.fromTo(".masonry-item img, .masonry-placeholder",
        { yPercent: 10 },
        {
          yPercent: 0,
          duration: 1.05, ease: "power3.inOut",
          stagger: { each: 0.13, from: "start" },
          scrollTrigger: { trigger: ".masonry-grid", start: "top 82%", toggleActions: "play none none none" },
        }
      );

      // ── Scroll-through: per-column parallax drift ────────────────────────
      // Each column moves at a different rate, creating a depth-field effect.
      const colOffsets = columns === 2 ? [-7, -13] : [-5, 0, -11];
      el.querySelectorAll<HTMLElement>(".masonry-col").forEach((col, i) => {
        if (!colOffsets[i]) return;
        gsap.to(col, {
          yPercent: colOffsets[i],
          ease: "none",
          scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: 1.4 },
        });
      });

      // ── Scroll-through: per-image zoom ───────────────────────────────────
      // Each image zooms gently as it travels through the viewport.
      // overflow-hidden on .masonry-item contains the scale so it never bleeds out.
      el.querySelectorAll<HTMLElement>(".masonry-item img").forEach((img) => {
        gsap.fromTo(img,
          { scale: 1 },
          {
            scale: 1.1,
            ease: "none",
            scrollTrigger: { trigger: img, start: "top bottom", end: "bottom top", scrub: 1.8 },
          }
        );
      });

    }, el);

    return () => ctx.revert();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Distribute photos round-robin into explicit column arrays
  const colPhotos: MasonryPhoto[][] = Array.from({ length: columns }, () => []);
  photos.forEach((photo, i) => colPhotos[i % columns]!.push(photo));

  const words = (headline ?? "").split(" ");

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-background overflow-hidden" id="gallery">
      <div className="max-w-5xl mx-auto">
        {(headline || subheadline) && (
          <div className="masonry-header text-center mb-10">
            {headline && (
              <h2 className="text-3xl font-bold text-foreground mb-2">
                {words.map((word, i) => (
                  <span key={i} className="inline-block overflow-hidden leading-tight align-bottom">
                    <span className="masonry-word inline-block">{word}</span>
                    {i < words.length - 1 && " "}
                  </span>
                ))}
              </h2>
            )}
            {subheadline && <p className="masonry-sub text-muted-foreground">{subheadline}</p>}
          </div>
        )}

        <div className="masonry-grid flex gap-2.5 items-start">
          {colPhotos.map((col, ci) => (
            <div key={ci} className={`masonry-col masonry-col-${ci} flex flex-col gap-2.5 flex-1`}>
              {col.map((photo, pi) => (
                <div
                  key={pi}
                  className="masonry-item overflow-hidden rounded-[var(--radius)] bg-muted"
                >
                  {photo.src ? (
                    <img
                      src={photo.src}
                      alt={photo.alt ?? ""}
                      loading="lazy"
                      className="w-full h-auto block"
                    />
                  ) : (
                    <div
                      className="masonry-placeholder w-full bg-muted flex items-center justify-center text-muted-foreground/40"
                      style={{ minHeight: pi % 3 === 0 ? "260px" : pi % 3 === 1 ? "180px" : "320px" }}
                    >
                      <span className="text-3xl">📷</span>
                    </div>
                  )}
                  {photo.caption && (
                    <p className="px-3 py-2 text-xs text-muted-foreground">{photo.caption}</p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
